import { useEffect, useState } from 'react'
import { fxReportsApi } from '../../services/api'
import DateRangePicker from '../../components/DateRangePicker'
import { FX_CATEGORY_LABELS } from './ExpensesTab'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface ExpenseByCategory {
  category: string
  total: number
}

interface CashPositionReport {
  cash_out: { total: number; expenses_total: number; purchases_total: number; by_category: ExpenseByCategory[] }
  cash_in: { total: number }
  net: number
}

/** Simpler than the coffee business's cash position in one way (every FX
 * Admin sale is cash, paid in full -- no "cobrado vs. pendiente" distinction
 * needed) but NOT simpler in another: cash_out here counts both operating
 * expenses AND inventory purchases (confirmed with the business owner --
 * purchase cost is plausibly this business's dominant cash outflow, unlike
 * the coffee business where that spend is folded into production-chain cost
 * allocation instead of a direct cash-position line item). */
export default function FxCashPositionTab() {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [report, setReport] = useState<CashPositionReport | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fxReportsApi
      .getCashPosition(startDate || undefined, endDate || undefined)
      .then((res) => {
        if (!cancelled) setReport(res.data)
      })
      .catch((err) => console.error('Error fetching FX cash position:', err))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [startDate, endDate])

  const { sortedData: sortedByCategory, sortConfig, requestSort } = useSortableData(report?.cash_out.by_category || [])

  return (
    <div>
      <div className="bg-cream-50 shadow rounded-lg p-4 mb-6">
        <DateRangePicker
          idPrefix="fx-cash-position-range"
          label="Rango de fechas:"
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      {loading ? (
        <div className="text-center py-8">Cargando...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Vendido</div>
              <div className="mt-2 text-2xl font-bold text-green-700">
                S/ {(report?.cash_in.total || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Comprado</div>
              <div className="mt-2 text-2xl font-bold text-red-600">
                S/ {(report?.cash_out.purchases_total || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Gastado</div>
              <div className="mt-2 text-2xl font-bold text-red-600">
                S/ {(report?.cash_out.expenses_total || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Neto</div>
              <div className={`mt-2 text-2xl font-bold ${(report?.net || 0) >= 0 ? 'text-espresso-800' : 'text-red-600'}`}>
                S/ {(report?.net || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <p className="text-xs text-espresso-500 mb-6">
            Neto = Vendido − Comprado − Gastado.
          </p>

          <h2 className="text-lg font-medium text-espresso-800 mb-3">Gastos por categoría</h2>
          {!report || report.cash_out.by_category.length === 0 ? (
            <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
              <p className="text-espresso-500">No hay gastos registrados en el rango seleccionado.</p>
            </div>
          ) : (
            <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-espresso-100">
                  <thead className="bg-cream-50">
                    <tr>
                      <SortableTh label="Categoría" sortKey="category" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableTh label="Total gastado" sortKey="total" sortConfig={sortConfig} onSort={requestSort} />
                    </tr>
                  </thead>
                  <tbody className="bg-cream-50 divide-y divide-espresso-100">
                    {sortedByCategory.map((row) => (
                      <tr key={row.category} className="hover:bg-cream-100">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-espresso-800">
                          {FX_CATEGORY_LABELS[row.category] || row.category}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                          S/ {Number(row.total).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
